// src/components/MobileAppOnboardingCard.jsx (v4 – local QR generation, no external service)

import React, { useEffect, useState } from "react";
import "../styles/MobileAppOnboardingCard.css";
import { MOBILE_APP_INSTALL_URL } from "../config";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Onboarding card encouraging users to install the mobile app.
 *
 * Used in principal dashboard and wizard. Shows QR code and direct download link.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.variant="principal"] - "principal" or other (affects copy)
 * @param {string|null} [props.storageKey=null] - localStorage key for dismissal persistence
 * @param {boolean} [props.dismissible=true] - Allow user to dismiss card
 * @param {function} [props.onDownload] - Callback when download is initiated
 * @returns {JSX.Element|null}
 */
const MobileAppOnboardingCard = ({
  variant = "principal",
  storageKey = null,
  dismissible = true,
  onDownload,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Load dismissed state from localStorage if applicable
  useEffect(() => {
    if (!storageKey || !dismissible) return;
    try {
      if (localStorage.getItem(storageKey) === "dismissed") {
        setDismissed(true);
      }
    } catch (err) {
      console.warn("localStorage unavailable:", err);
    }
  }, [storageKey, dismissible]);

  const handleDismiss = () => {
    if (!dismissible) return;
    setDismissed(true);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, "dismissed");
      } catch (err) {
        console.warn("Failed to save dismissed state:", err);
      }
    }
  };

  const handleDownloadClick = () => {
    onDownload?.();
  };

  if (dismissed) return null;

  const isPrincipal = variant === "principal";

  return (
    <section className="mobile-app-card" aria-labelledby="mobile-app-card-title">
      <div className="mobile-app-card__glow" aria-hidden="true" />

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss mobile app prompt"
          className="mobile-app-card__dismiss"
        >
          ×
        </button>
      )}

      <h3 id="mobile-app-card-title" className="mobile-app-card__title">
        {isPrincipal
          ? "Get the GhrianGhraffy App on your phone"
          : "Snap photos instantly with the GhrianGhraffy App"}
      </h3>

      <p className="mobile-app-card__body">
        {isPrincipal
          ? "Install the GhrianGhraffy App once and start sharing photos instantly."
          : "You'll use this App (not the website) to take photos. One-tap capture, offline drafts, and instant upload."}
      </p>

      <div className="mobile-app-card__qr-wrapper">
        <div className="mobile-app-card__qr-image" aria-label="QR code to install GhrianGhraffy app">
          <QRCodeCanvas
            value={MOBILE_APP_INSTALL_URL}
            includeMargin={false}
            level="M"
          />
        </div>

        <p className="mobile-app-card__qr-note">
          <strong>Scan with your phone camera</strong> → installs instantly
        </p>
      </div>

      <div className="mobile-app-card__button-row">
        <a
          href={MOBILE_APP_INSTALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDownloadClick}
          className="btn btn-primary mobile-app-card__download-button"
        >
          📱 Download GhrianGhraffy for Android
        </a>
      </div>

      <p className="mobile-app-card__footnote">
        iOS app coming very soon — we’ll email you the moment it’s live!
      </p>
    </section>
  );
};

export default MobileAppOnboardingCard;
